// src/pages/PublicSector/PublicSectorCompliance.jsx
import React, { useEffect } from "react";
import SEO from "../../components/SEO";

const track = (name, props = {}) => {
  if (typeof window !== "undefined" && window.clarity) {
    window.clarity("event", name, props);
  }
};

const PublicSectorCompliance = () => {
  useEffect(() => {
    track("public_sector_page_view", { page: "compliance" });
  }, []);

  return (
    <main className="bg-white">
      <SEO
        title="Compliance & Standardization | myBOQ (MH PWD SSR)"
        description="Learn how myBOQ supports SSR-aligned structure, standardized reporting, and documentation clarity for Maharashtra PWD estimation workflows."
        keywords="myBOQ compliance, SSR standardization, Maharashtra PWD SSR software, rate analysis, lead calculation, royalty, testing, schedule b marathi"
        image="https://myboq.in/ssrlogo3.png"
        url="https://myboq.in/public-sector/compliance"
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1B3A] via-[#0A1020] to-black" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-20 py-14 sm:py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 text-white border border-white/10 px-3 py-1 rounded-full text-xs font-semibold">
              🧾 <span>SSR-aligned</span>
              <span className="text-orange-300">Standardization</span>
            </div>

            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              Compliance & Standardization
              <span className="text-orange-400"> — SSR-Aligned Workflows</span>
            </h1>

            <p className="mt-4 text-white/80 text-base sm:text-lg leading-relaxed">
              Digitally structured estimation aligned with Maharashtra PWD SSR formats and consistent reporting practices
              for clarity, review-readiness, and repeatable documentation.
            </p>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="max-w-7xl mx-auto px-6 lg:px-20 py-12 sm:py-14">
        <div className="grid lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4">
            <div className="sticky top-24 rounded-2xl border border-gray-200 bg-white shadow-sm p-5">
              <div className="text-sm font-semibold text-gray-900">Compliance coverage</div>
              <div className="mt-3 space-y-2 text-sm text-gray-700">
                <div className="rounded-lg border border-gray-200 p-3">Maharashtra PWD SSR item structure</div>
                <div className="rounded-lg border border-gray-200 p-3">Rate analysis alignment</div>
                <div className="rounded-lg border border-gray-200 p-3">Lead-based calculations</div>
                <div className="rounded-lg border border-gray-200 p-3">Royalty & testing sections</div>
                <div className="rounded-lg border border-gray-200 p-3">Standard report formats</div>
              </div>

              <div className="mt-5 rounded-xl bg-orange-50 border border-orange-100 p-4">
                <div className="text-sm font-semibold text-gray-900">Current scope</div>
                <p className="mt-1 text-sm text-gray-700">
                  myBOQ currently supports MH PWD SSR workflows. Additional departments & states will be added as new
                  catalogs without disrupting the core workflow.
                </p>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-8 space-y-10">
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-7">
              <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900">
                SSR-Aligned Structure (Maharashtra Focus)
              </h2>
              <p className="mt-2 text-gray-600">
                myBOQ is built around SSR-driven estimation patterns and outputs commonly used in Maharashtra PWD.
              </p>

              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
                  <div className="text-sm font-semibold text-gray-900">Supported areas</div>
                  <ul className="mt-2 text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>SSR item mapping & structured entry</li>
                    <li>Rate analysis structure</li>
                    <li>Lead-based cost adjustments</li>
                    <li>Royalty classification</li>
                    <li>Testing rate integration</li>
                  </ul>
                </div>
                <div className="rounded-xl bg-white border border-gray-200 p-4">
                  <div className="text-sm font-semibold text-gray-900">What this enables</div>
                  <ul className="mt-2 text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Recognizable SSR-style output structure</li>
                    <li>Systematic organization of calculations</li>
                    <li>Repeatable reporting across users</li>
                    <li>Cleaner review and verification</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-7">
              <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900">Standardized Report Outputs</h2>
              <p className="mt-2 text-gray-600">
                Generate consistent outputs across projects and teams — designed for clarity and internal review cycles.
              </p>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {[
                  "Detailed Estimate",
                  "Abstract",
                  "Rate Analysis",
                  "Lead Calculation Sheet",
                  "Royalty Section",
                  "Testing Summary",
                  "Schedule B (Marathi)",
                  "Bank Estimate Format",
                ].map((r) => (
                  <div key={r} className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
                    {r}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-7">
              <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900">
                Documentation Clarity & Review-Readiness
              </h2>
              <p className="mt-2 text-gray-600">
                myBOQ does not replace official verification systems; it improves the structure and clarity of estimation
                documentation used during preparation and review.
              </p>

              <div className="mt-5 grid sm:grid-cols-3 gap-3">
                {[
                  "Structured estimate building",
                  "Clear item-wise breakdown",
                  "Organized measurement entries",
                  "Controlled revision duplication",
                  "Uniform reporting templates",
                  "Cleaner internal verification",
                ].map((t) => (
                  <div key={t} className="rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-700">
                    {t}
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-xl bg-[#0B1B3A]/5 border border-[#0B1B3A]/10 p-4">
                <div className="text-sm font-semibold text-gray-900">Standardization benefits</div>
                <ul className="mt-2 text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li>Reduces variability between engineers</li>
                  <li>Encourages repeatable estimation workflow</li>
                  <li>Improves documentation quality for audits and reviews</li>
                </ul>
              </div>
            </div>

            <div className="rounded-2xl border border-orange-200 bg-orange-50 p-6 sm:p-7">
              <h3 className="text-lg font-extrabold text-gray-900">Positioning line (use everywhere)</h3>
              <p className="mt-2 text-gray-800 font-semibold">
                Digitizing Maharashtra PWD Estimation — Structured, Standardized, Scalable.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PublicSectorCompliance;